<script setup lang="ts">
import type { Props, Column } from '@/shared/ui/ColumnsBlock/model/types/columnsBlock';

const props = withDefaults(defineProps<Props>(), {
  countInColumn: 0,
  columnsCount: 0,
  items: () => [],
  gap: 0,
  keyName: '',
  isShowButton: false,
  indexOfLastItem: 0,
  withWrap: false,
  isSubway: false,
  isHorizontalSort: false,
});

const emit = defineEmits<{
  (e: 'show'): void;
}>();

const isMobile: boolean = useIsMobile();

const columnItems: ComputedRef<Column[]> = computed(() => {
  const colsCount = props.withWrap
    ? Math.ceil(props.items.length / props.countInColumn)
    : props.columnsCount;

  const columns: Column[] = [];
  for (let i = 0; i < colsCount; i += 1) {
    const isLastIteration = i + 1 === colsCount;
    const startIndex = i * props.countInColumn;
    const endIndex = isLastIteration ? props.items.length : (i + 1) * props.countInColumn;

    columns.push({
      id: i,
      items: props.items.slice(startIndex, endIndex),
    });
  }

  return columns;
});

const isShowLastItem = (rowIndex: number, itemIndex: number): boolean => {
  if (!props.isShowButton || itemIndex !== props.countInColumn - 1) return false;
  const countRow = rowIndex + 1;
  return props.indexOfLastItem === countRow * props.countInColumn - 1;
};

const showMoreHandler = (): void => {
  emit('show');
};

const styles: ComputedRef<string> = computed(() =>
  props.withWrap
    ? 'display:grid; grid-template-rows: repeat(1, 1fr); row-gap: 20px; column-gap: 40px;'
    : `gap: ${props.gap}px`,
);
</script>

<template>
  <div
    :class="[
      'columns-block',
      `_${keyName}`,
      { _mobile: isMobile },
      { _show: isShowButton },
      { _subway: isSubway },
      { _horizontal: isHorizontalSort },
    ]"
    :style="styles"
  >
    <ul
      v-for="(column, index) of columnItems"
      :key="column.id"
      class="columns-block__column"
    >
      <li
        v-for="(item, i) in column.items"
        :key="item?.id"
        :class="['columns-block__column-item', item?.classMod]"
      >
        <slot :item="item" :index="i">
          <NuxtLink
            v-if="!isShowLastItem(index, i)"
            :to="item?.link"
            class="columns-block__item-link"
          >
            <AppIcon
              v-if="!!item?.iconName"
              class="columns-block__item-icon"
              :name="item?.iconName"
            />
            <span
              v-if="isSubway"
              class="columns-block__metro-color"
              :style="{ backgroundColor: `${item?.subwayColor}` }"
            ></span>
            {{ item?.name }}
          </NuxtLink>
          <button
            v-if="isShowLastItem(index, i)"
            class="columns-block__button"
            type="button"
            aria-label="Показать ещё"
            @click.prevent="showMoreHandler"
          >
            Показать ещё
            <AppIcon class="columns-block__button-icon" name="ArrowDownSmall" />
          </button>
        </slot>
      </li>
    </ul>
  </div>
</template>

<style lang="less">
.columns-block {
  display: flex;

  @media @tablet {
    gap: 45px;
  }

  &__column {
    flex: 1 1 auto;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: @size-x5;
  }

  &._horizontal &__column {
    display: grid;
    flex-direction: row;
    grid-template-columns: repeat(4, 1fr);
  }

  &._show &__column:has(._hide) {
    display: none;
  }

  &__column-item {
    display: flex;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
    color: @color-elephant;
    flex-direction: column;
    gap: @size-x5;

    @media @tablet {
      gap: @size-x4;
    }
  }

  &._cities &__column-item {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  &._show &__column-item._hide {
    display: none;
  }

  &._show &__item-link {
    &:hover {
      color: @color-persimmon;
    }
  }

  &._specializations &__item-link {
    display: flex;
    gap: 10px;
    text-decoration-color: @color-persimmon;
    align-items: center;

    &:hover {
      color: @color-persimmon;
    }

    &:hover .columns-block__item-icon path {
      fill: @color-persimmon;
    }
  }

  &._specializations &__column {
    gap: 30px;
  }

  &__item-icon {
    width: @size-x5;
    height: @size-x5;
  }

  &__metro-color {
    width: @size-x2;
    height: @size-x2;
    border-radius: 100%;
    margin-right: 5px;
  }

  &._cities &__item-link {
    .color-change-hover();
  }

  &._dropdown &__item-link {
    .color-change-hover();
  }

  &._show &__item-link {
    display: flex;
    align-items: center;
  }

  &._show {
    row-gap: @size-x5;
  }

  &__button {
    .color-change-hover();

    display: flex;
    align-items: center;
    width: 100%;
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;

    &:hover {
      color: @color-persimmon;
    }
  }

  &__button-icon path {
    stroke: @color-persimmon;
  }

  &._mobile._horizontal &__column {
    grid-template-columns: repeat(2, 1fr);
  }

  &._mobile._subway &__item-link {
    position: relative;
    padding-left: 13px;
  }

  &._mobile &__metro-color {
    position: absolute;
    left: 0;
  }
}
</style>
